import { useRef, useEffect, forwardRef, useImperativeHandle, useState } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Link as LinkIcon, Palette } from "lucide-react";

const SANITIZE_OPTS = {
  ALLOWED_TAGS: ["p", "br", "b", "strong", "i", "em", "u", "ul", "ol", "li", "span", "div", "a", "h1", "h2", "h3", "table", "thead", "tbody", "tr", "th", "td", "font"],
  ALLOWED_ATTR: ["href", "target", "rel", "colspan", "rowspan", "style", "color"],
};

export function sanitizeHtml(html) {
  return DOMPurify.sanitize(html || "", SANITIZE_OPTS);
}

// Whole documents built by the server or from Admin templates (service agreement terms,
// schedules, summaries): ordinary HTML and classes are kept; scripts, event handlers,
// javascript: links, styles and form controls are removed.
export function sanitizeDocHtml(html) {
  return DOMPurify.sanitize(html || "", {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["style", "form", "input", "button", "textarea", "select"],
  });
}

// True when the HTML has no visible text/content (so we can show a placeholder).
export function isEmptyHtml(html) {
  if (!html) return true;
  const txt = html.replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").trim();
  return txt.length === 0;
}

// Lightweight rich-text editor (bold / italic / lists, optional headings & links) storing safe HTML.
export const RichTextEditor = forwardRef(function RichTextEditor(
  { value, onChange, disabled, testid, headings = false, links = false, placeholder = "Add notes…" },
  extRef
) {
  const ref = useRef(null);
  const savedRange = useRef(null);
  const [colourOpen, setColourOpen] = useState(false);

  // Colour palette: label → hex value
  const COLOURS = [
    { label: "Default",    hex: null },
    { label: "Red",        hex: "#dc2626" },
    { label: "Orange",     hex: "#ea580c" },
    { label: "Amber",      hex: "#d97706" },
    { label: "Green",      hex: "#16a34a" },
    { label: "Teal",       hex: "#0d9488" },
    { label: "Blue",       hex: "#2563eb" },
    { label: "Violet",     hex: "#7c3aed" },
    { label: "Pink",       hex: "#db2777" },
    { label: "Slate",      hex: "#475569" },
  ];

  // Load incoming HTML when it changes externally. Skip while the editor is focused
  // (typing) so we never reset the caret position mid-edit.
  useEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    const clean = sanitizeHtml(value || "");
    if (el.innerHTML !== clean) el.innerHTML = clean;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = () => onChange && onChange(sanitizeHtml(ref.current?.innerHTML || ""));

  // Remember where the caret was so toolbar/chip clicks can insert at that spot.
  const rememberRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreRange = () => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (savedRange.current && el.contains(savedRange.current.commonAncestorContainer)) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    } else {
      // Fall back to the very end of the content.
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  // Exposed so parents can inject placeholder tokens (e.g. {staff_name}) at the caret.
  useImperativeHandle(extRef, () => ({
    insertText(text) {
      if (disabled) return;
      restoreRange();
      document.execCommand("insertText", false, text);
      rememberRange();
      emit();
    },
  }));

  const exec = (cmd) => {
    if (disabled) return;
    restoreRange();
    document.execCommand(cmd, false, null);
    rememberRange();
    emit();
  };

  const block = (tag) => {
    if (disabled) return;
    restoreRange();
    // Toggle: if the current block is already this heading, drop back to a paragraph.
    const cur = (document.queryCommandValue("formatBlock") || "").toLowerCase();
    document.execCommand("formatBlock", false, cur === tag ? "p" : tag);
    rememberRange();
    emit();
  };

  const addLink = () => {
    if (disabled) return;
    const url = window.prompt("Link URL", "https://");
    if (!url) return;
    restoreRange();
    const sel = window.getSelection();
    const hasSelection = sel && !sel.isCollapsed;
    if (hasSelection) {
      document.execCommand("createLink", false, url);
    } else {
      document.execCommand("insertHTML", false, `<a href="${url}">${url}</a>`);
    }
    rememberRange();
    emit();
  };

  const applyColour = (hex) => {
    if (disabled) return;
    restoreRange();
    if (hex) {
      document.execCommand("foreColor", false, hex);
    } else {
      document.execCommand("foreColor", false, "inherit");
    }
    rememberRange();
    emit();
    setColourOpen(false);
  };

  const HEADING_STYLES = "[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-2 [&_h1]:mb-1 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-1.5 [&_h3]:mb-1";
  const LINK_STYLES = "[&_a]:text-teal-700 [&_a]:underline";

  if (disabled) {
    return (
      <div
        className={`mt-1 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm text-slate-700 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-1 ${HEADING_STYLES} ${LINK_STYLES}`}
        data-testid={testid}
      >
        {isEmptyHtml(value)
          ? <span className="text-slate-400">No additional information.</span>
          : <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} />}
      </div>
    );
  }

  const btn = "h-7 w-7 inline-flex items-center justify-center rounded text-slate-600 hover:bg-slate-200/70 transition-colors";

  return (
    <div className="mt-1 rounded-md border border-slate-200 bg-white overflow-hidden focus-within:border-slate-400 transition-colors" data-testid={testid}>
      <div className="flex items-center gap-0.5 border-b border-slate-100 bg-slate-50/70 px-1.5 py-1 flex-wrap">
        <button type="button" className={btn} title="Bold" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")} data-testid={`${testid}-bold`}><Bold className="h-3.5 w-3.5" /></button>
        <button type="button" className={btn} title="Italic" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")} data-testid={`${testid}-italic`}><Italic className="h-3.5 w-3.5" /></button>
        {headings && <>
          <span className="mx-1 h-4 w-px bg-slate-200" />
          <button type="button" className={btn} title="Heading" onMouseDown={(e) => e.preventDefault()} onClick={() => block("h2")} data-testid={`${testid}-h2`}><Heading2 className="h-3.5 w-3.5" /></button>
          <button type="button" className={btn} title="Subheading" onMouseDown={(e) => e.preventDefault()} onClick={() => block("h3")} data-testid={`${testid}-h3`}><Heading3 className="h-3.5 w-3.5" /></button>
        </>}
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <button type="button" className={btn} title="Bullet list" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")} data-testid={`${testid}-ul`}><List className="h-3.5 w-3.5" /></button>
        <button type="button" className={btn} title="Numbered list" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertOrderedList")} data-testid={`${testid}-ol`}><ListOrdered className="h-3.5 w-3.5" /></button>
        {links && <>
          <span className="mx-1 h-4 w-px bg-slate-200" />
          <button type="button" className={btn} title="Add link" onMouseDown={(e) => e.preventDefault()} onClick={addLink} data-testid={`${testid}-link`}><LinkIcon className="h-3.5 w-3.5" /></button>
        </>}
        <span className="mx-1 h-4 w-px bg-slate-200" />
        <div className="relative">
          <button type="button" className={btn} title="Text colour" onMouseDown={(e) => e.preventDefault()}
            onClick={() => setColourOpen((o) => !o)} data-testid={`${testid}-colour`}>
            <Palette className="h-3.5 w-3.5" />
          </button>
          {colourOpen && (
            <div className="absolute left-0 top-8 z-50 rounded-lg border border-slate-200 bg-white shadow-lg p-2 w-[168px]"
              onMouseDown={(e) => e.preventDefault()}>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5 px-0.5">Text colour</div>
              <div className="grid grid-cols-5 gap-1">
                {COLOURS.map(({ label, hex }) => (
                  <button key={label} type="button" title={label} onClick={() => applyColour(hex)}
                    className="h-6 w-6 rounded border border-slate-200 hover:scale-110 transition-transform focus:outline-none focus:ring-1 focus:ring-slate-400 flex items-center justify-center"
                    style={{ backgroundColor: hex || "#ffffff" }}
                    data-testid={`${testid}-colour-${label.toLowerCase()}`}>
                    {!hex && <span className="text-[9px] text-slate-500 font-bold leading-none">A</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={() => { rememberRange(); emit(); setColourOpen(false); }}
        onKeyUp={rememberRange}
        onMouseUp={rememberRange}
        data-placeholder={placeholder}
        className={`rte min-h-[80px] px-3 py-2 text-sm text-slate-700 outline-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-1 ${HEADING_STYLES} ${LINK_STYLES} empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400`}
      />
    </div>
  );
});
